import {
  createTheme,
  colors,
  PaletteColor,
  PaletteColorOptions,
  alpha,
} from "@mui/material";

const LAYOUT = {
  topBarHeight: "50px",
};

declare module "@mui/material/styles" {
  interface Theme {
    layout: typeof LAYOUT;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    layout: typeof LAYOUT;
  }
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    narrow: true;
    wide: true;
  }
  interface Palette {
    accessory: PaletteColor;
    accessoryBlue: PaletteColor;
    tertiary: PaletteColor;
    tertiaryBlue: PaletteColor;
    tertiary2: PaletteColor;
  }
  interface PaletteOptions {
    accessory: PaletteColorOptions;
    accessoryBlue: PaletteColorOptions;
    tertiary: PaletteColorOptions;
    tertiaryBlue: PaletteColorOptions;
    tertiary2: PaletteColorOptions;
  }
  interface TypographyVariants {
    display0: React.CSSProperties;
    display0Bold: React.CSSProperties;
    h1Bold: React.CSSProperties;
    display1: React.CSSProperties;
    display1Bold: React.CSSProperties;
    h2Bold: React.CSSProperties;
    h2SemiBold: React.CSSProperties;
    display2Caps: React.CSSProperties;
    display2: React.CSSProperties;
    display2Bold: React.CSSProperties;
    h3Bold: React.CSSProperties;
    label2Caps: React.CSSProperties;
    label3Caps: React.CSSProperties;
    body1: React.CSSProperties;
    body1Bold: React.CSSProperties;
    body2: React.CSSProperties;
    body2Bold: React.CSSProperties;
    body3: React.CSSProperties;
    body3Bold: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    display0?: React.CSSProperties;
    display0Bold?: React.CSSProperties;
    h1Bold?: React.CSSProperties;
    display1: React.CSSProperties;
    display1Bold?: React.CSSProperties;
    h2Bold?: React.CSSProperties;
    h2SemiBold?: React.CSSProperties;
    display2Caps?: React.CSSProperties;
    display2?: React.CSSProperties;
    display2Bold?: React.CSSProperties;
    h3Bold?: React.CSSProperties;
    label2Caps?: React.CSSProperties;
    label3Caps?: React.CSSProperties;
    body1?: React.CSSProperties;
    body1Bold?: React.CSSProperties;
    body2?: React.CSSProperties;
    body2Bold?: React.CSSProperties;
    body3?: React.CSSProperties;
    body3Bold?: React.CSSProperties;
  }
}

export const palette = {
  darkGreen: "#006B66",
  midGreen: "#D5F2E4",
  lightGreen: "#E9F8F2",
  lightestGreen: "#F4FBF8",

  purple: "#5A52A7",
  navy: "#222A5C",
  darkestBlue: "#181D40",
  darkBlue: "#0E56AB",
  blue: "#0074DF",
  highlightBlue: "#ADDBE8",
  midBlue: "#BDEFF8",
  lightBlue: "#EBF9FB",
  lightestBlue: "#F3FCFD",

  darkGray: "#6A6783",
  midGray: "#D7D5E0",
  lightGray: "#F0EEF5",
  lightestGray: "#F9F8FB",
  white: "#FFFFFF",

  red: "#C52737",
  orange: "#8C3500",
  midOrange: "#FFEDCE",
  lightOrange: "#FFF4E2",
  lightestOrange: "#FFF8EE",

  // deprecated names, use the ones above instead
  blue4: "#BDEFF8",
  blueHighlight: "#DDF4F8",
};

declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
    tertiaryBlue: true;
    accessory: true;
    accessoryBlue: true;
    tertiary2: true;
    containedTertiary: true;
    containedTertiary2: true;
  }

  interface ButtonPropsVariantOverrides {
    link: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display0: true;
    display0Bold: true;
    h1Bold: true;
    display1: true;
    display1Bold: true;
    h2Bold: true;
    h2SemiBold: true;
    display2Caps: true;
    display2: true;
    display2Bold: true;
    h3Bold: true;
    label2Caps: true;
    label3Caps: true;
    body1: true;
    body1Bold: true;
    body2: true;
    body2Bold: true;
    body3: true;
    body3Bold: true;
  }
}

const theme = createTheme({
  palette: {
    background: {
      default: "#FFFFFF",
      paper: colors.common.white,
    },
    primary: {
      main: palette.blue,
      contrastText: colors.common.white,
    },
    secondary: {
      main: palette.navy,
      contrastText: colors.common.white,
    },
    tertiary: {
      main: palette.lightGray,
      contrastText: palette.navy,
    },
    tertiaryBlue: {
      main: palette.blue4,
      contrastText: palette.navy,
    },
    tertiary2: {
      main: palette.midBlue,
      contrastText: palette.navy,
    },
    accessory: {
      main: palette.lightGray,
      contrastText: palette.navy,
    },
    accessoryBlue: {
      main: palette.blue4,
      contrastText: palette.navy,
    },
    text: {
      primary: palette.navy,
      secondary: palette.darkGray,
    },
    error: {
      main: palette.red,
    },
    warning: {
      main: palette.orange,
    },
    ...palette,
  },
  typography: {
    fontFamily: "Poppins", // TODO: fallback fonts
    display0: {
      fontSize: "26px",
      fontWeight: 400,
    },
    display0Bold: {
      fontSize: "26px",
      fontWeight: 700,
      lineHeight: "140%",
    },
    h1: {
      fontSize: "20px",
      fontWeight: 400,
    },
    h1Bold: {
      fontSize: "20px",
      fontWeight: 700,
    },
    display1Bold: {
      fontSize: "20px",
      fontWeight: 700,
    },
    display1: {
      fontSize: "20px",
      fontWeight: 400,
    },
    h2: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "150%",
    },
    h2Bold: {
      fontSize: "16px",
      fontWeight: 700,
    },
    h2SemiBold: {
      fontSize: "16px",
      fontWeight: 600,
    },
    display2: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "150%",
    },
    display2Bold: {
      fontSize: "16px",
      fontWeight: 700,
    },
    display2Caps: {
      fontSize: "16px",
      textTransform: "uppercase",
      fontWeight: 500,
    },
    h3: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "150%",
    },
    h3Bold: {
      fontSize: "14px",
      fontWeight: 700,
    },
    body1: {
      fontSize: "14px",
    },
    body1Bold: {
      fontSize: "14px",
      fontWeight: 700,
    },
    body2: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "140%",
    },
    body2Bold: {
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "140%",
    },
    label2Caps: {
      fontSize: "12px",
      textTransform: "uppercase",
      fontWeight: 600,
    },
    body3: {
      fontSize: "10px",
      fontWeight: 400,
    },
    body3Bold: {
      fontSize: "10px",
      fontWeight: 600,
    },
    label3Caps: {
      fontSize: "10px",
      textTransform: "uppercase",
      fontWeight: 500,
    },
  },
  layout: LAYOUT,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      narrow: 0,
      wide: 900,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: LAYOUT.topBarHeight,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          border: "1px solid",
          borderColor: palette.midGray,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          backgroundColor: palette.midGray,
          padding: "4px 16px",
        },
        title: {
          fontSize: 12,
          fontWeight: 700,
        },
        action: {
          "& .MuiIconButton-root": {
            color: palette.navy,
          },

          "& .MuiSvgIcon-root": {
            fontSize: "0.7em",
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          switch (ownerState.variant) {
            case "outlined":
              return {
                marginBottom: "16px",
                fontSize: "14px",
                paddingTop: "24px",

                ".MuiTextField-root": {
                  marginBottom: ownerState.error ? "2px" : "25px",
                },

                ".MuiInputBase-root": {
                  minHeight: "40px",

                  borderStyle: "solid",
                  border: "1px solid",
                  borderRadius: 0,
                  position: "relative",
                  backgroundColor: theme.palette.common.white,
                  transition: theme.transitions.create([
                    "border-color",
                    "box-shadow",
                  ]),

                  "&.Mui-disabled": {
                    color: palette.darkGray,
                  },

                  ...(ownerState.error
                    ? {
                        borderColor: palette.red,
                        borderWidth: "2px",
                        ".MuiInputBase-input": {
                          padding: "9px 11px",
                        },
                      }
                    : {
                        borderColor: palette.midGray,
                        borderWidth: "1px",
                        ".MuiInputBase-input": {
                          padding: "10px 12px",
                        },
                        "&:focus, &:focus-within": {
                          borderColor: theme.palette.primary.main,
                          borderWidth: "2px",
                          ".MuiInputBase-input": {
                            padding: "9px 11px",
                          },
                        },
                      }),
                },

                ".MuiFormLabel-root": {
                  color: palette.darkGray,
                  marginLeft: "-12px",
                  marginTop: "12px",

                  "&.Mui-focused": {
                    color: palette.darkGray,
                  },
                },

                ".MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },

                ".MuiOutlinedInput-root": {
                  padding: 0,
                },
              };
            case "standard":
              return {
                ".MuiFormLabel-root": {
                  marginTop: "8px",
                },
              };
            default:
              return {};
          }
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          // TODO: move to formcontrol?
          "&::placeholder": {
            color: palette.darkGray,
            opacity: 1,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&.Mui-disabled": {
            color: palette.darkGray,
            textFillColor: palette.darkGray,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // We actually want the label text to be 12px, but shrink: true is required to position the label correctly
          // (see https://mui.com/material-ui/react-text-field/#shrink)
          // so we're compensating for the .75 scale that shrinking applies
          fontSize: "16px",
          fontWeight: 600,
          "&.Mui-disabled": {
            color: palette.darkGray,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "inherit",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.navy,
          textDecorationColor: palette.navy,
          "&:hover": {
            textDecoration: "underline",
            color: "#181D40",
            textDecorationColor: "#181D40",
          },
          "&:focus,:focus-visible": {
            outline: `2px solid ${palette.blue}`,
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: palette.navy,
          fontWeight: 600,
          "&.Mui-active": {
            fontWeight: 600,
          },
        },
        root: {
          padding: 0,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        lineVertical: {
          borderColor: palette.lightGray,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          strokeWidth: "2px",
          stroke: palette.navy,
          fill: colors.common.white,

          circle: {
            r: "10px",
          },

          ".MuiStepIcon-text": {
            fill: palette.navy,
          },

          "&.Mui-active": {
            fill: palette.navy,

            ".MuiStepIcon-text": {
              fill: colors.common.white,
            },
          },
        },
        text: {
          fontWeight: 600,
          strokeWidth: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 999,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#F0EEF5",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.white,

          // this bit is for the grey outline around the checkbox.
          // unfortunately, we can't use a simple border here due to the spacing between the input,
          // svg, and path elements (it ends up with a gap between the icon and the border).
          // so instead, we can add an svg path outline to get the same effect:
          "& .MuiSvgIcon-root > path": {
            outline: `1px solid ${palette.midGray}`,
          },
          "&.Mui-checked": {
            color: palette.darkGreen,
          },
        },
        colorSecondary: {
          color: palette.navy,
          "&.Mui-checked": {
            color: palette.navy,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorSecondary: {
          color: palette.navy,
          "&.Mui-checked": {
            color: palette.navy,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        contained: ({ ownerState, theme }) => {
          switch (ownerState.color) {
            case "primary":
              return {
                fontSize: "14px",
                letterSpacing: "0.48px",
                fontWeight: 600,
                height: "40px",
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  background: palette.darkBlue,
                },
              };
            case "secondary":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                "&:hover": {
                  background: palette.darkestBlue,
                },
              };
            case "tertiary":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "tertiaryBlue":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.tertiaryBlue.main,
                color: theme.palette.tertiaryBlue.contrastText,

                "&:hover": {
                  background: palette.highlightBlue,
                },
              };
            case "accessory":
              return {
                fontSize: "11px",
                fontWeight: 500,
                height: "27px",
                letterSpacing: "0.4px",
                background: theme.palette.accessory.main,
                color: theme.palette.accessory.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "accessoryBlue":
              return {
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.4px",
                background: theme.palette.accessoryBlue.main,
                color: theme.palette.accessoryBlue.contrastText,
                "&:hover": {
                  background: palette.highlightBlue,
                },
              };
            case "tertiary2":
              return {
                background: theme.palette.tertiary2.main,
                color: theme.palette.tertiary2.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "error":
              return {
                background: theme.palette.error.main,
                color: theme.palette.error.contrastText,
                "&:hover": {
                  background: palette.orange,
                },
              };
            default:
              return {
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
          }
        },
        outlined: {
          borderColor: "unset",
        },
        root: {
          borderRadius: 0,
          boxShadow: "none",
          whiteSpace: "nowrap",
          "&.Mui-disabled": {
            background: palette.midGray,
            color: palette.darkGray,
          },
          border: "1px solid transparent",
          "&:focus,:focus-visible": {
            outline: `2px solid ${palette.darkBlue}`,
            outlineOffset: "0px",
            border: "1px solid white",
          },
        },
      },
      variants: [
        {
          props: { variant: "link" },
          style: {
            textTransform: "none",
            textDecoration: "underline",
            background: "none",
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
              background: "none",
            },
            "&:focus,:focus-visible": {
              outline: `2px solid ${palette.blue}`,
              outlineOffset: "2px",
            },
          },
        },
      ],
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          "&:focus,:focus-visible": {
            outline: `2px solid ${palette.darkBlue}`,
            outlineOffset: "0px",
          },
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          "&:focus,:focus-visible": {
            backgroundColor: `${palette.midGray}`,
            "&.Mui-selected": {
              backgroundColor: `${palette.midGray}`,
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: palette.lightGray,
          "&:before": {
            display: "none",
          },
          padding: "10px 20px",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          fontWeight: 600,
          size: "16px",
          margin: "20px 0",
        },
        expandIconWrapper: {
          "& .MuiSvgIcon-root": {
            width: "1.5em",
            height: "1.5em",
          },
        },
        root: {
          fontFamily: "inherit",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          switch (ownerState.color) {
            case "tertiary":
              return {
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
              };
          }
          switch (ownerState.severity) {
            case "warning":
              return { backgroundColor: palette.lightOrange };
          }
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&:focus-within": {
            outline: `2px solid ${palette.darkBlue}`,
            outlineOffset: "-3px",
          },
          "& .MuiButtonBase-root:hover": {
            backgroundColor: alpha(palette.darkGray, 0.1),

            "&.Mui-checked": {
              backgroundColor: alpha(palette.blue, 0.2),
            },
          },
          "& .MuiSwitch-thumb": {
            backgroundColor: palette.darkGray,
          },
          "& .MuiSwitch-track": {
            backgroundColor: palette.midGray,
            opacity: 1,
          },
          "& .MuiButtonBase-root.Mui-checked+.MuiSwitch-track": {
            backgroundColor: alpha(palette.blue, 0.6),
            opacity: 1,
          },
          "& .Mui-checked > .MuiSwitch-thumb": {
            backgroundColor: palette.navy,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          display0: "span",
          display0Bold: "span",
          h1: "h1",
          h1Bold: "h1",
          display1: "span",
          display1Bold: "span",
          h2: "h2",
          h2Bold: "h2",
          h2SemiBold: "h2",
          display2: "span",
          display2Bold: "span",
          display2Caps: "span",
          h3: "h3",
          h3Bold: "h3",
          h4: "h4",
          h5: "h5",
          h6: "h6",
          label2Caps: "span",
          label3Caps: "span",
        },
      },
    },
  },
});

export default theme;
